/**
 * TaskPoller - Central polling for all active tasks
 *
 * This component runs at the app level and:
 * 1. Periodically fetches active tasks from the API (discovers new tasks)
 * 2. Polls for status updates on all tasks in the TaskManager context
 *
 * This ensures tasks update regardless of which page the user is on,
 * and automatically discovers tasks created from any source.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useTaskManager } from '@/contexts/TaskManagerContext';
import { useTaskPollingControl } from '@/contexts/TaskPollingControlContext';
import { useApiContext } from '@/lib/api-context';
import { apiClient } from '@/lib/api-client';
import {
  getEffectivePollingInterval,
  DEFAULT_TASK_POLLING_CONFIG,
  isTerminalStatus,
} from '@/lib/polling';
import { RateLimitedError } from '@/lib/rate-limit';
import { usePageVisibility } from '@/hooks/usePageVisibility';
import { parseUTCDate } from '@/lib/utils';
import { getTaskExecutionKey } from '@/lib/task-lifecycle';

// How often to check for new tasks from the API (in ms)
const DISCOVERY_INTERVAL = 10_000;

function isSyntheticTaskId(taskId: string): boolean {
  return taskId.startsWith('batch-');
}

export function TaskPoller() {
  const { activeTasks, updateTask, registerTask } = useTaskManager();
  const { isDiscoveryPaused } = useTaskPollingControl();
  const { isReady: isApiReady, token } = useApiContext();
  const isVisible = usePageVisibility();
  const canPollApi = isApiReady && Boolean(token);

  // Refs for polling state
  const activeTasksRef = useRef(activeTasks);
  activeTasksRef.current = activeTasks;

  const attemptCountsRef = useRef<Map<string, number>>(new Map());
  const statusPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const discoveryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const discoveryInFlightRef = useRef(false);
  const statusInFlightRef = useRef(false);

  // ============================================================================
  // Task Discovery: Periodically fetch active tasks from API
  // This catches new tasks created from any source
  // ============================================================================
  const discoverNewTasks = useCallback(async () => {
    if (!canPollApi || isDiscoveryPaused || discoveryInFlightRef.current) return;
    discoveryInFlightRef.current = true;
    const requestStartedAt = Date.now();

    try {
      const [pendingResponse, processingResponse] = await Promise.all([
        apiClient.listTasks({ status: 'pending', limit: 50 }),
        apiClient.listTasks({ status: 'processing', limit: 50 }),
      ]);

      const allActiveTasks = [...pendingResponse.data, ...processingResponse.data];

      for (const task of allActiveTasks) {
        registerTask(task, task.entity_name, { requestStartedAt });
      }
    } catch (error) {
      if (!(error instanceof RateLimitedError)) {
        console.warn('Failed to discover tasks:', error);
      }
    } finally {
      discoveryInFlightRef.current = false;
    }
  }, [canPollApi, isDiscoveryPaused, registerTask]);

  // ============================================================================
  // Status Polling: Poll status of known active tasks
  // ============================================================================
  const pollTaskStatuses = useCallback(async () => {
    if (!canPollApi || statusInFlightRef.current) return;

    const currentTasks = activeTasksRef.current;
    const tasksToCheck = Array.from(currentTasks.values()).filter(
      (task) => !isTerminalStatus(task.status) && !isSyntheticTaskId(task.taskId)
    );

    if (tasksToCheck.length === 0) return;
    statusInFlightRef.current = true;

    // Poll tasks sequentially to avoid bursting the rate limit.
    // With N active tasks, parallel polling sends N requests simultaneously;
    // serial polling sends 1 at a time and stops early on rate limit.
    try {
      for (const task of tasksToCheck) {
        try {
          const requestStartedAt = Date.now();
          const data = await apiClient.getTask(task.taskId);

          updateTask(
            task.taskId,
            {
              status: data.status,
              progress: data.progress,
              progressMessage: data.progress_message,
              startedAt: data.started_at ? (parseUTCDate(data.started_at) ?? undefined) : undefined,
              completedAt: data.completed_at
                ? (parseUTCDate(data.completed_at) ?? undefined)
                : undefined,
              payload: data.payload,
              result: data.result,
              error: data.error,
              attempts: data.attempts,
            },
            { requestStartedAt, expectedExecutionKey: getTaskExecutionKey(task) }
          );

          // Update attempt count for backoff
          const currentAttempts = attemptCountsRef.current.get(task.taskId) ?? 0;
          if (!isTerminalStatus(data.status)) {
            attemptCountsRef.current.set(task.taskId, currentAttempts + 1);
          } else {
            attemptCountsRef.current.delete(task.taskId);
          }
        } catch (error) {
          if (error instanceof RateLimitedError) {
            break; // Stop polling remaining tasks — we're rate-limited
          }
          console.error(`Failed to poll task ${task.taskId}:`, error);
        }
      }
    } finally {
      statusInFlightRef.current = false;
    }
  }, [canPollApi, updateTask]);

  // Calculate polling interval based on task attempt counts and active task count.
  // Scales the interval with task count to keep total request rate under the
  // server's 100 req/min limit (each cycle sends activeCount sequential requests).
  const getStatusPollInterval = useCallback(() => {
    const currentTasks = activeTasksRef.current;
    let minInterval = DEFAULT_TASK_POLLING_CONFIG.maxInterval;
    let activeCount = 0;

    for (const task of currentTasks.values()) {
      if (!isTerminalStatus(task.status)) {
        activeCount++;
        const attempts = attemptCountsRef.current.get(task.taskId) ?? 0;
        const interval = getEffectivePollingInterval(attempts, DEFAULT_TASK_POLLING_CONFIG);
        minInterval = Math.min(minInterval, interval);
      }
    }

    // Budget-aware floor: each cycle sends activeCount requests (now serial).
    // Other sources (discovery, batch detail, credits) add ~30 req/min overhead.
    // To keep status polling under ~60 req/min: interval >= activeCount * 1s.
    const budgetFloor = activeCount * 1000;

    return Math.max(minInterval, budgetFloor);
  }, []);

  // ============================================================================
  // Discovery Effect: Run task discovery on interval
  // ============================================================================
  useEffect(() => {
    if (!canPollApi || !isVisible || isDiscoveryPaused) {
      if (discoveryIntervalRef.current) {
        clearInterval(discoveryIntervalRef.current);
        discoveryIntervalRef.current = null;
      }
      return;
    }

    // Initial discovery
    void discoverNewTasks();

    // Set up discovery interval
    discoveryIntervalRef.current = setInterval(() => {
      void discoverNewTasks();
    }, DISCOVERY_INTERVAL);

    return () => {
      if (discoveryIntervalRef.current) {
        clearInterval(discoveryIntervalRef.current);
        discoveryIntervalRef.current = null;
      }
    };
  }, [canPollApi, isVisible, isDiscoveryPaused, discoverNewTasks]);

  // ============================================================================
  // Status Polling Effect: Poll known tasks for status updates
  // ============================================================================
  useEffect(() => {
    const hasActiveTasks = activeTasks.size > 0;

    if (!canPollApi || !hasActiveTasks || !isVisible) {
      if (statusPollIntervalRef.current) {
        clearInterval(statusPollIntervalRef.current);
        statusPollIntervalRef.current = null;
      }
      return;
    }

    // Initial poll
    void pollTaskStatuses();

    // Set up polling with dynamic interval
    const scheduleNextPoll = () => {
      if (statusPollIntervalRef.current) {
        clearInterval(statusPollIntervalRef.current);
      }

      const interval = getStatusPollInterval();
      statusPollIntervalRef.current = setInterval(() => {
        void pollTaskStatuses();
        scheduleNextPoll(); // Reschedule with potentially new interval
      }, interval);
    };

    scheduleNextPoll();

    return () => {
      if (statusPollIntervalRef.current) {
        clearInterval(statusPollIntervalRef.current);
        statusPollIntervalRef.current = null;
      }
    };
  }, [activeTasks.size, canPollApi, isVisible, pollTaskStatuses, getStatusPollInterval]);

  return null;
}
